import React, { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw,
  Plus,
  HelpCircle,
  XCircle,
  Shield,
  Clock,
  CheckCircle,
  AlertTriangle,
  Calendar,
  BarChart3,
} from 'lucide-react';
import { useToast } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import ContractService from '../../services/contractService';
import RenewalAlertsService from '../../services/renewalAlertsService';
import { useHelp } from '../../context/HelpContext';
import PageHelp from '../help/PageHelp';

const formatDateForInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const formatDateLabel = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const getDefaultFormData = () => ({
  contractId: '',
  expiryDate: '',
  renewalStartDate: '',
  renewalEndDate: '',
  autoRenew: false,
  autoRenewIntervalMonths: 12,
  alertThresholds: '90,60,30',
  alertRecipients: '',
  notes: '',
});

const ContractRenewalsAlerts = () => {
  const { success, error: showError } = useToast();
  const { openPageHelp } = useHelp();

  const [renewalAlerts, setRenewalAlerts] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formVisible, setFormVisible] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [formData, setFormData] = useState(getDefaultFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState(null);

  const loadContracts = async () => {
    try {
      const response = await ContractService.getUserContracts({
        includeInactive: true,
      });
      const availableContracts = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setContracts(availableContracts);
    } catch (error) {
      console.error('Failed to load contracts for renewal selection', error);
      showError('Unable to load contracts for renewals and alerts');
    }
  };

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const response = await RenewalAlertsService.list('all');
      setRenewalAlerts(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch renewal alerts', error);
      showError('Failed to load renewal alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContracts();
    loadAlerts();
  }, []);

  const filteredAlerts = useMemo(() => {
    if (activeTab === 'pending') {
      return renewalAlerts.filter((alert) => alert.alertStatus === 'pending');
    }
    if (activeTab === 'sent') {
      return renewalAlerts.filter((alert) => alert.alertStatus === 'sent');
    }
    if (activeTab === 'past-due') {
      return renewalAlerts.filter((alert) => alert.isPastDue);
    }
    return renewalAlerts;
  }, [activeTab, renewalAlerts]);

  const summaryStats = useMemo(() => {
    const pending = renewalAlerts.filter(
      (alert) => alert.alertStatus === 'pending'
    ).length;
    const sent = renewalAlerts.filter(
      (alert) => alert.alertStatus === 'sent'
    ).length;
    const pastDue = renewalAlerts.filter((alert) => alert.isPastDue).length;
    const autoRenew = renewalAlerts.filter((alert) => alert.autoRenew).length;
    const nextAlert = renewalAlerts
      .filter((alert) => alert.nextAlertAt)
      .map((alert) => new Date(alert.nextAlertAt))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime())[0];

    return {
      total: renewalAlerts.length,
      pending,
      sent,
      pastDue,
      autoRenew,
      nextAlert: nextAlert ? nextAlert.toLocaleDateString() : 'None',
    };
  }, [renewalAlerts]);

  // KPI cards (visual only) — icons and styling only, no logic changes
  const kpiCards = [
    {
      value: summaryStats.total,
      label: 'Total Configurations',
      icon: Shield,
      iconColor: '#2D7DD2',
      iconBg: '#D6EFFF',
    },
    {
      value: summaryStats.pending,
      label: 'Pending Alerts',
      icon: Clock,
      iconColor: '#F59E0B',
      iconBg: '#FEF3C7',
    },
    {
      value: summaryStats.sent,
      label: 'Sent Alerts',
      icon: CheckCircle,
      iconColor: '#059669',
      iconBg: '#D1FAE5',
    },
    {
      value: summaryStats.pastDue,
      label: 'Past Due',
      icon: AlertTriangle,
      iconColor: '#EF4444',
      iconBg: '#FEE2E2',
    },
    {
      value: summaryStats.autoRenew,
      label: 'Auto-Renew Enabled',
      icon: RefreshCw,
      iconColor: '#7C3AED',
      iconBg: '#EDE9FE',
    },
    {
      value: summaryStats.nextAlert,
      label: 'Next Alert',
      icon: Calendar,
      iconColor: '#06B6D4',
      iconBg: '#DFF9FF',
    },
  ];

  const renderTab = (tab) => {
    const IconComponent = tab.icon;
    const isActive = activeTab === tab.id;

    return (
      <button
        key={tab.id}
        onClick={() => setActiveTab(tab.id)}
        className={`
        flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium w-full border-b-2 transition-colors duration-200
        ${
          isActive
            ? 'border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
            : 'border-transparent text-[var(--text-secondary)] hover:text-slate-800 hover:bg-slate-100'
        }
      `}
      >
        {IconComponent && <IconComponent size={18} />}
        <span className="whitespace-nowrap">{tab.label}</span>
      </button>
    );
  };

  const handleRefresh = () => {
    loadAlerts().then(() => success('Renewal alerts refreshed'));
  };

  const handleOpenForm = (mode, alert = null) => {
    setFormMode(mode);
    if (alert) {
      setSelectedAlert(alert);
      setFormData({
        contractId: alert.contractId,
        expiryDate: formatDateForInput(alert.expiryDate),
        renewalStartDate: formatDateForInput(
          alert.renewalDates?.[0]?.startDate
        ),
        renewalEndDate: formatDateForInput(alert.renewalDates?.[0]?.endDate),
        autoRenew: Boolean(alert.autoRenew),
        autoRenewIntervalMonths: alert.autoRenewIntervalMonths || 12,
        alertThresholds: (alert.alertThresholds || []).join(','),
        alertRecipients: (alert.alertRecipients || []).join(', '),
        notes: alert.notes || '',
      });
    } else {
      setSelectedAlert(null);
      setFormData(getDefaultFormData());
    }
    setFormVisible(true);
  };

  const handleCloseForm = () => {
    setFormVisible(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (!formData.contractId) {
      return showError('Select a contract before saving the renewal alert');
    }
    if (!formData.expiryDate) {
      return showError('Expiry date is required');
    }

    const thresholds = formData.alertThresholds
      .split(/[,;]+/)
      .map((item) => Number(item.trim()))
      .filter((value) => !Number.isNaN(value) && value > 0);

    const recipients = formData.alertRecipients
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    const renewalDates = [];
    if (formData.renewalStartDate || formData.renewalEndDate) {
      renewalDates.push({
        startDate: formData.renewalStartDate || formData.expiryDate,
        endDate: formData.renewalEndDate || formData.expiryDate,
      });
    }

    const payload = {
      expiryDate: formData.expiryDate,
      autoRenew: formData.autoRenew,
      autoRenewIntervalMonths: Number(formData.autoRenewIntervalMonths) || 12,
      alertThresholds: thresholds.length ? thresholds : undefined,
      alertRecipients: recipients.length ? recipients : undefined,
      renewalDates: renewalDates.length ? renewalDates : undefined,
      notes: formData.notes,
    };

    setIsSubmitting(true);
    try {
      let result;
      if (formMode === 'create') {
        result = await RenewalAlertsService.create(formData.contractId, payload);
        if (result?.success === false) {
          showError(result?.message || 'Failed to create renewal alert');
          return;
        }
        success('Renewal alert configuration saved');
      } else if (selectedAlert) {
        result = await RenewalAlertsService.update(
          formData.contractId,
          selectedAlert._id,
          payload
        );
        if (result?.success === false) {
          showError(result?.message || 'Failed to update renewal alert');
          return;
        }
        success('Renewal alert configuration updated');
      }
      setFormVisible(false);
      await loadAlerts();
    } catch (error) {
      console.error('Error saving renewal alert', error);
      showError(error?.response?.data?.message || error?.message || 'Unable to save renewal alert');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (alert) => {
    setAlertToDelete(alert);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!alertToDelete) return;
    try {
      await RenewalAlertsService.remove(
        alertToDelete.contractId,
        alertToDelete._id
      );
      success('Renewal alert removed');
      await loadAlerts();
    } catch (error) {
      console.error('Error deleting renewal alert', error);
      showError(error?.message || 'Failed to delete renewal alert');
    } finally {
      setShowDeleteModal(false);
      setAlertToDelete(null);
    }
  };

  const tabs = [
    { id: 'all', label: 'All Configurations', icon: Shield },
    { id: 'pending', label: 'Pending Alerts', icon: Clock },
    { id: 'sent', label: 'Sent Alerts', icon: CheckCircle },
    { id: 'past-due', label: 'Past Due', icon: AlertTriangle },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  const columns = [
    {
      key: 'contractTitle',
      label: 'Contract',
      render: (_, alert) => (
        <div>
          <div className="fw-bold" style={{ fontSize: '14px' }}>
            {alert.contract?.title || alert.contractId}
          </div>
          <div className="text-muted" style={{ fontSize: '12px' }}>
            {alert.contractId}
          </div>
        </div>
      ),
    },
    {
      key: 'counterparty',
      label: 'Counterparty',
      render: (_, alert) => alert.contract?.counterparty || '—',
    },
    {
      key: 'expiryDate',
      label: 'Expiry Date',
      render: (value) => formatDateLabel(value),
    },
    {
      key: 'daysUntilExpiry',
      label: 'Days Left',
      render: (value) => (
        <span style={{ color: value <= 0 ? '#dc2626' : '#059669' }}>
          {value <= 0 ? 'Expired' : `${value} days`}
        </span>
      ),
    },
    {
      key: 'nextAlertAt',
      label: 'Next Alert',
      render: (value) => formatDateLabel(value),
    },
    {
      key: 'alertStatus',
      label: 'Alert Status',
      render: (value) => (
        <span
          className={`badge ${
            value === 'sent' ? 'badge-success' : 'badge-warning'
          }`}
        >
          {value?.toUpperCase() || 'PENDING'}
        </span>
      ),
    },
    {
      key: 'alertThresholds',
      label: 'Thresholds',
      render: (value) => (Array.isArray(value) ? value.join(', ') : '—'),
    },
    {
      key: 'autoRenew',
      label: 'Auto-Renew',
      render: (value) => (value ? 'Enabled' : 'Manual'),
    },
  ];

  const tableData = useMemo(() => (
    filteredAlerts.map((alert) => ({
      ...alert,
      id: alert._id,
      contractTitle: alert.contract?.title || alert.contractId || '—',
      counterparty: alert.contract?.counterparty || '—',
      alertThresholds: Array.isArray(alert.alertThresholds) ? alert.alertThresholds.join(', ') : (alert.alertThresholds || '—')
    }))
  ), [filteredAlerts]);

  const headerActions = [
    {
      label: '',
      icon: <RefreshCw size={18} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh data',
      disabled: loading,
    },
    {
      label: 'New Renewal & Alert',
      icon: <Plus size={18} />,
      onClick: () => handleOpenForm('create'),
      variant: 'primary',
    },
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: () => openPageHelp('contract-renewals'),
      variant: 'ghost',
      tooltip: 'Open renewal help',
    },
  ];

  return (
    <div className="contract-renewals-alerts">
      <PageBreadcrumb
        items={[
          { label: 'Contracts', path: '/contracts' },
          { label: 'Contract Renewals & Alerts' },
        ]}
      />
      <PageHeader
        title="Contract Renewals & Alerts"
        subtitle="Manage renewal dates, configure alert thresholds, and keep expiration workflows aligned."
        actions={headerActions}
      />

      {/* Summary Cards – StatCard reference styling (object-driven) */}
      <div className="grid-modern-4 gap-6 !m-8">
        {kpiCards.map((card, index) => {
          const IconComponent = card.icon;

          return (
            <div
              key={index}
              className="relative border border-[var(--border-color)] bg-white"
            >
              {/* Top Accent Line */}
              <div className="h-1 w-full bg-[var(--primary-color)]" />

              {/* Content */}
              <div className="relative flex items-center gap-3 p-4">
                {/* Icon */}
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center"
                  style={{ background: card.iconBg }}
                >
                  <IconComponent size={20} style={{ color: card.iconColor }} />
                </div>

                {/* Text */}
                <div>
                  <div className="text-[20px] font-bold text-[var(--text-primary)]">
                    {card.value}
                  </div>
                  <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                    {card.label}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="m-8">
        <div className="w-full mb-6 bg-white border border-slate-200 overflow-hidden">
          <div className="w-full flex items-center bg-white border-b border-slate-200">
            {tabs.map(renderTab)}
          </div>
        </div>

        {activeTab === 'analytics' ? (
          <div
            className="analytics-panel"
            style={{
              marginTop: '24px',
              background: '#fff',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              padding: '24px',
            }}
          >
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div>
                <h4>Alert Delivery</h4>
                <p>
                  {summaryStats.sent} alerts have been sent,{' '}
                  {summaryStats.pending} remain pending.
                </p>
              </div>
              <div>
                <h4>Renewal Health</h4>
                <p>
                  {summaryStats.pastDue} contracts are past due;{' '}
                  {summaryStats.autoRenew} are configured for automatic renewal.
                </p>
              </div>
            </div>
            <div
              style={{
                marginTop: '16px',
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  flex: '1 1 220px',
                  padding: '16px',
                  borderRadius: '10px',
                  background: 'var(--primary-color)',
                  color: '#fff',
                }}
              >
                <strong>Ensure Coverage</strong>
                <p style={{ marginTop: '8px' }}>
                  Keep alerts ahead of every stage by keeping thresholds at
                  30/60/90 days.
                </p>
              </div>
              <div
                style={{
                  flex: '1 1 220px',
                  padding: '16px',
                  borderRadius: '10px',
                  border: '1px solid var(--border-color)',
                }}
              >
                <strong>Auto-Renew Impact</strong>
                <p style={{ marginTop: '8px' }}>
                  Auto-renewing contracts are{' '}
                  {summaryStats.autoRenew > 0
                    ? 'shielded from lapses'
                    : 'waiting for manual updates'}
                  .
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              marginTop: '24px',
              background: '#fff',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '24px',
            }}
          >
            <DataTable
              title="Renewal & Alert Configurations"
              data={tableData}
              columns={columns}
              searchable
              sortable
              exportable
              pagination
              pageSize={10}
              showActions
              showCheckboxes={false}
              onEdit={(row) => handleOpenForm('edit', row)}
              onDelete={handleDelete}
              loading={loading}
              searchPlaceholder="Search contracts, statuses, or alert thresholds"
              emptyMessage="No renewal alerts configured yet. Click 'New Renewal & Alert' to get started."
            />
          </div>
        )}
      </div>

      {formVisible && (
        <div
          className="modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
        >
          <form
            className="modal-card"
            onSubmit={handleFormSubmit}
            style={{
              background: '#fff',
              borderRadius: '16px',
              width: 'min(720px, 90vw)',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '32px',
              boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
              position: 'relative',
              border: '1px solid #e2e8f0',
            }}
          >
            <button
              type="button"
              onClick={handleCloseForm}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#64748b',
              }}
            >
              <XCircle size={24} />
            </button>
            <h3 style={{ marginBottom: '8px', fontSize: '20px', fontWeight: '600', color: '#0f172a' }}>
              {formMode === 'create'
                ? 'Create Renewal & Alert'
                : 'Update Renewal & Alert'}
            </h3>
            <p style={{ color: '#64748b', marginBottom: '28px', fontSize: '14px' }}>
              Save renewal sequencing, alert thresholds, and notification
              routing from a single form.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '20px',
                paddingBottom: '20px',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#334155' }}>Contract</span>
                <select
                  value={formData.contractId}
                  onChange={(e) =>
                    handleFormChange('contractId', e.target.value)
                  }
                  required
                  disabled={formMode === 'edit'}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">Select a contract</option>
                  {contracts.map((contract) => (
                    <option
                      key={contract.contractId}
                      value={contract.contractId}
                    >
                      {contract.title} — {contract.counterparty}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#334155' }}>Expiry Date</span>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) =>
                    handleFormChange('expiryDate', e.target.value)
                  }
                  required
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#334155' }}>Renewal Start</span>
                <input
                  type="date"
                  value={formData.renewalStartDate}
                  onChange={(e) =>
                    handleFormChange('renewalStartDate', e.target.value)
                  }
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                  disabled={formMode === 'view'}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#334155' }}>Renewal End</span>
                <input
                  type="date"
                  value={formData.renewalEndDate}
                  onChange={(e) =>
                    handleFormChange('renewalEndDate', e.target.value)
                  }
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
              </label>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '20px',
                marginTop: '20px',
                paddingBottom: '20px',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <input
                  type="checkbox"
                  checked={formData.autoRenew}
                  onChange={(e) =>
                    handleFormChange('autoRenew', e.target.checked)
                  }
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#334155' }}>Enable automatic renewal</span>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#334155' }}>Auto Renew Interval (months)</span>
                <input
                  type="number"
                  min="1"
                  value={formData.autoRenewIntervalMonths}
                  onChange={(e) =>
                    handleFormChange('autoRenewIntervalMonths', e.target.value)
                  }
                  disabled={!formData.autoRenew || formMode === 'view'}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
              </label>
            </div>

            <div
              style={{
                marginTop: '20px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '20px',
                paddingBottom: '20px',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#334155' }}>Alert Thresholds (days)</span>
                <input
                  type="text"
                  value={formData.alertThresholds}
                  onChange={(e) =>
                    handleFormChange('alertThresholds', e.target.value)
                  }
                  placeholder="e.g. 90,60,30"
                  disabled={formMode === 'view'}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#334155' }}>Alert Recipients</span>
                <input
                  type="text"
                  value={formData.alertRecipients}
                  onChange={(e) =>
                    handleFormChange('alertRecipients', e.target.value)
                  }
                  placeholder="email1@example.com, email2@example.com"
                  disabled={formMode === 'view'}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
              </label>
            </div>

            <label style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#334155' }}>Notes</span>
              <textarea
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                rows={3}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
                disabled={formMode === 'view'}
              />
            </label>

            <div
              style={{
                marginTop: '28px',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
              }}
            >
              <button
                type="button"
                onClick={handleCloseForm}
                disabled={isSubmitting}
                className="btn btn-outline"
                style={{
                  padding: '10px 20px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              {formMode !== 'view' && (
                <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                  {isSubmitting
                  ? 'Saving...'
                  : formMode === 'create'
                  ? 'Create Configuration'
                  : 'Update Configuration'}
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Renewal Alert"
        message={`Remove the alert for ${alertToDelete?.contractId}? This will stop all scheduled notifications.`}
        confirmText="Delete"
        cancelText="Cancel"
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
      />

      <PageHelp />
    </div>
  );
};

export default ContractRenewalsAlerts;